import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { layoutFuerBlatt, readLayout } from "@/office/excel/layout";
import { festeZahlen } from "@/office/excel/formeln";
import {
  allTables,
  filledSheets,
  formulaCells,
  hasDefaultSheetName,
  hasDefaultTableName,
  isError,
  allCells,
  readWorkbook,
} from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

export const lektionWeitergabe: Lesson = {
  id: "weitergabe",
  title: "Weitergeben, ohne sich zu blamieren",
  summary: "Was eine Excel-Datei über ihren Autor verrät – und was vorher weg sollte.",
  durationMinutes: 12,
  learningGoals: [
    "Einen Druckbereich festlegen",
    "Eine Arbeitsmappe vor der Weitergabe prüfen",
    "Die eigene Datei mit den Augen eines Fremden lesen",
  ],
  steps: [
    {
      id: "druckbereich",
      title: "Der Druckbereich",
      explanation: [
        "Manchmal soll nur ein Teil eines Blattes aufs Papier: die Auswertung, nicht " +
          "die Rohdaten daneben; die Zusammenfassung, nicht die Hilfsspalten. Dafür " +
          "gibt es den Druckbereich unter „Seitenlayout“ → „Druckbereich“ → " +
          "„Druckbereich festlegen“.",
        "Er wird mit der Datei gespeichert und gilt ab dann für jeden Ausdruck – auch " +
          "für den nächsten Kollegen, der die Datei öffnet und sich wundert, warum die " +
          "Hälfte fehlt. Deshalb gehört zum Festlegen die Notiz irgendwo im Blatt, " +
          "dass es einen gibt, oder wenigstens die Gewohnheit, vor dem Drucken einmal " +
          "in der Vorschau nachzusehen.",
        "Mehrere Bereiche gleichzeitig sind möglich – Strg gedrückt halten beim " +
          "Markieren –, führen aber dazu, dass jeder Bereich auf einer eigenen Seite " +
          "landet. Was nach einer eleganten Lösung aussieht, ergibt oft einen " +
          "unerwartet zerstückelten Ausdruck.",
        "Aufgehoben wird der Druckbereich im selben Menü. Und für den einmaligen Fall " +
          "gibt es eine Alternative, die nichts speichert: markieren, dann im " +
          "Druckdialog unter „Einstellungen“ die Option „Auswahl drucken“.",
      ],
      keyPoints: [
        "Seitenlayout → Druckbereich → Druckbereich festlegen.",
        "Er wird gespeichert und gilt auch für alle anderen.",
        "Einmalig: markieren, dann Strg + P → „Auswahl drucken“.",
      ],
      task: {
        instruction:
          "Lege für eines deiner Blätter einen Druckbereich fest, der nur den Teil " +
          "umfasst, der wirklich aufs Papier soll.",
        hint: "Bereich markieren, dann Seitenlayout → Druckbereich → Druckbereich festlegen.",
        check: async () => {
          const layout = await readLayout();

          if (layout.pageLayouts === null) {
            return nichtPruefbar(
              "Der Druckbereich eines Blattes",
              "1.9",
              "Sieh unter „Seitenlayout“ → „Druckbereich“ nach, ob dort „Druckbereich aufheben“ anwählbar ist.",
            );
          }

          const mitBereich = layout.pageLayouts.filter((eintrag) => eintrag.printArea !== null);
          if (mitBereich.length === 0) {
            return fail(
              "Auf keinem Blatt ist ein Druckbereich festgelegt – gedruckt wird jeweils die ganze " +
                "benutzte Fläche.",
              "Markiere den Bereich, der aufs Papier soll, und wähle Seitenlayout → Druckbereich → " +
                "Druckbereich festlegen.",
            );
          }

          const erstes = mitBereich[0];
          return pass(
            `Auf „${erstes.sheetName}“ ist ${erstes.printArea} als Druckbereich festgelegt. ` +
              "Denk daran: Das gilt auch für alle anderen, die die Datei öffnen.",
          );
        },
      },
    },
    {
      id: "dokumentpruefung",
      title: "Was eine Datei alles mitliefert",
      explanation: [
        "Eine Excel-Datei enthält mehr, als auf dem Bildschirm zu sehen ist. " +
          "Ausgeblendete Blätter mit der Kalkulationsgrundlage. Kommentare mit " +
          "internen Rückfragen. Der Name des Autors und aller, die die Datei je " +
          "bearbeitet haben. Kopf- und Fußzeilen mit dem Dateipfad – und damit oft " +
          "mit dem Namen des Kunden, für den die Datei ursprünglich gebaut wurde.",
        "„Datei“ → „Informationen“ → „Auf Probleme überprüfen“ → „Dokument prüfen“ " +
          "durchsucht die Datei nach genau diesen Dingen und listet auf, was gefunden " +
          "wurde. Zu jedem Punkt gibt es eine Schaltfläche „Alle entfernen“.",
        "Vorsicht ist bei dieser Schaltfläche geboten: Was entfernt wird, ist weg, " +
          "und ein Rückgängig gibt es nicht. Deshalb gilt die Regel, die Datei vorher " +
          "unter einem neuen Namen zu speichern – die bereinigte Fassung geht raus, " +
          "die vollständige bleibt.",
        "Für die Weitergabe an Menschen, die nichts ändern sollen, ist ohnehin PDF " +
          "das bessere Format: „Datei“ → „Exportieren“ → „PDF/XPS-Dokument erstellen“. " +
          "Ein PDF hat keine ausgeblendeten Blätter, keine Formeln und keine " +
          "Kommentare. Und es sieht bei allen gleich aus – ein Excel-Ausdruck tut das " +
          "nicht, weil die Seitenumbrüche vom eingestellten Drucker abhängen.",
      ],
      keyPoints: [
        "Datei → Informationen → Auf Probleme überprüfen → Dokument prüfen.",
        "Vorher unter neuem Namen speichern – das Entfernen ist unwiderruflich.",
        "Für Empfänger, die nichts ändern sollen: PDF statt xlsx.",
      ],
    },
    {
      id: "abschluss",
      title: "Abschlussübung: die Datei mit fremden Augen",
      explanation: [
        "Zum Schluss der Blick, den man sich angewöhnen sollte, bevor eine Datei das " +
          "Haus verlässt. Er dauert zwei Minuten und geht die Punkte durch, die in " +
          "diesem Kurs vorkamen.",
        "Tragen alle Blätter und Tabellen sprechende Namen? Steht in den Formeln " +
          "irgendwo eine getippte Konstante, die eigentlich in eine Zelle gehört? " +
          "Zeigt eine Zelle einen Fehlerwert? Sind die Rohdaten eine Tabelle, sodass " +
          "die Auswertungen mitwachsen?",
        "Diese Fragen sind keine Formalität. Sie entscheiden darüber, ob jemand " +
          "anders mit der Datei arbeiten kann – und ob sie in sechs Monaten noch " +
          "stimmt, wenn dreihundert Zeilen dazugekommen sind und der Steuersatz sich " +
          "geändert hat.",
        "Die Prüfung unten geht genau diese Punkte durch. Sie ist absichtlich streng, " +
          "und sie prüft nichts, was nicht vorher im Kurs vorkam.",
      ],
      keyPoints: [
        "Sprechende Namen für Blätter und Tabellen.",
        "Keine getippten Konstanten in Formeln.",
        "Keine Fehlerwerte, Rohdaten als Tabelle.",
      ],
      task: {
        instruction:
          "Geh deine Arbeitsmappe ein letztes Mal durch: sprechende Blatt- und " +
          "Tabellennamen, keine getippten Konstanten in Formeln, keine Fehlerwerte, " +
          "Rohdaten als Tabelle und eine druckfertige Seiteneinrichtung.",
        hint: "Strg + # zeigt alle Formeln auf einmal – so findest du getippte Konstanten am schnellsten.",
        check: async () => {
          const mappe = await readWorkbook();
          const layout = await readLayout();

          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Der Inhalt und die Formeln der Zellen",
              "1.4",
              "Geh die Punkte aus der Aufgabenstellung selbst durch – Strg + # hilft beim Aufspüren " +
                "getippter Konstanten.",
              true,
            );
          }

          if (filledSheets(mappe).length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst die Daten aus den vorigen Modulen ein.");
          }

          const blattnamen = mappe.sheets.filter(hasDefaultSheetName);
          const tabellen = allTables(mappe);
          const tabellennamen = tabellen.filter(hasDefaultTableName);
          const formeln = formulaCells(mappe);
          const konstanten = formeln.filter((zelle) => festeZahlen(zelle.formula).length > 0);
          const fehler = allCells(mappe).filter(isError);

          const kriterien = [
            criterion(
              blattnamen.length === 0
                ? "Alle Blätter tragen einen sprechenden Namen"
                : `Noch mit Standardnamen: ${blattnamen.map((blatt) => `„${blatt.name}“`).join(", ")}`,
              blattnamen.length === 0,
            ),
            criterion(
              tabellen.length > 0
                ? tabellennamen.length === 0
                  ? `Die Rohdaten liegen als Tabelle vor (${tabellen.map((tabelle) => `„${tabelle.name}“`).join(", ")})`
                  : `Tabelle noch mit Standardnamen: ${tabellennamen.map((tabelle) => `„${tabelle.name}“`).join(", ")}`
                : "Die Rohdaten liegen noch nicht als Tabelle vor",
              tabellen.length > 0 && tabellennamen.length === 0,
            ),
            criterion(
              konstanten.length === 0
                ? `Keine der ${formeln.length} Formeln enthält eine getippte Konstante`
                : `Getippte Konstante in ${konstanten.slice(0, 2).map((zelle) => `${zelle.address} („${zelle.formula}“)`).join(", ")}`,
              formeln.length > 0 && konstanten.length === 0,
            ),
            criterion(
              fehler.length === 0
                ? "Keine Zelle zeigt einen Fehlerwert"
                : `Fehlerwerte in ${fehler.slice(0, 3).map((zelle) => zelle.address).join(", ")}`,
              fehler.length === 0,
            ),
          ];

          // Die Seiteneinrichtung nur dann als Kriterium führen, wenn sie in
          // dieser Excel-Version überhaupt lesbar ist. Ein Punkt, den niemand
          // erfüllen kann, würde die Aufgabe unbestehbar machen.
          if (layout.pageLayouts !== null) {
            const datenblatt = tabellen.length > 0 ? layoutFuerBlatt(layout, tabellen[0].sheetName) : null;
            const druckfertig =
              datenblatt !== null &&
              datenblatt.printTitleRows !== null &&
              datenblatt.fitToWidth !== null &&
              datenblatt.fitToWidth > 0;
            kriterien.push(
              criterion(
                druckfertig
                  ? "Das Datenblatt ist druckfertig eingerichtet"
                  : "Auf dem Datenblatt fehlt noch Wiederholungszeile oder Breitenanpassung",
                druckfertig,
              ),
            );
          }

          return fromCriteria(kriterien, {
            success:
              "Die Arbeitsmappe hält dem fremden Blick stand: sprechende Namen, Formeln ohne " +
              "getippte Konstanten, keine Fehlerwerte und Rohdaten, die mitwachsen. Damit ist der " +
              "Kurs durch.",
            failure: "Ein paar Punkte fehlen noch für eine Datei, die man aus der Hand geben kann.",
            hint:
              layout.pageLayouts === null
                ? "Die Seiteneinrichtung lässt sich in dieser Excel-Version nicht auslesen (dafür " +
                  "braucht es ExcelApi 1.9) und ist deshalb nicht Teil der Liste – prüfe sie selbst " +
                  "über Strg + P."
                : "Jeder Punkt gehört zu einem Modul dieses Kurses. Wo etwas fehlt, lohnt der Blick " +
                  "zurück in das jeweilige Modul.",
          });
        },
      },
    },
  ],
};
